import React, { useEffect, useState, useRef, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles, Flower } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { ReikiBackground } from './components/ReikiBackground';
import { GiftBackground } from './components/GiftBackground';
import { InfraestructuraSistemica } from './components/InfraestructuraSistemica';
import { DiferencialHibrido } from './components/DiferencialHibrido';
import { Hero } from './components/Hero';
import { PurchaseCTA } from './components/PurchaseCTA';
import { PainPoints } from './components/PainPoints';
import { SystemicNetwork } from './components/SystemicNetwork';
import { UpsellPurchase } from './components/UpsellPurchase';
import { PortalOffer } from './components/PortalOffer';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [to, isInView]);
  return <motion.span ref={ref} data-id="element-0" data-mp="3dl4l7ovdxlwe">{rounded}</motion.span>;
}
const testimonials = ['https://cdn.magicpatterns.com/uploads/m5wcH3RSYvXnvgvU2Au4x2/ChatGPT_Image_28_may_2026,_11_55_17.webp', 'https://cdn.magicpatterns.com/uploads/cga1WmjCDT5J8Xycvb6AR9/ChatGPT_Image_28_may_2026,_11_56_35.webp', 'https://cdn.magicpatterns.com/uploads/e7THeU2n1KzAsmxmTswuuQ/ChatGPT_Image_28_may_2026,_12_00_35.webp', 'https://cdn.magicpatterns.com/uploads/6hR9Tj6wJpSkqGb1CyDkGJ/ChatGPT_Image_28_may_2026,_12_04_32.webp', 'https://cdn.magicpatterns.com/uploads/dGkLPuYukHKUF2Rnm1wDsC/ChatGPT_Image_28_may_2026,_11_51_23.webp'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(4);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '1051253568074079';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-blue-50 text-blue-950 font-sans selection:bg-blue-200 selection:text-blue-950" data-id="element-1" data-mp="c8rf1rwtj7w1">
      <SystemicNetwork data-id="element-2" data-mp="19uyiuzlasw7c" />
      <ReikiBackground data-id="element-3" data-mp="19vhtdv8mcibc" />
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-4" data-mp="19tnfh0vrlqgr" />

      {/* Hero Section */}
      <Hero data-id="element-5" data-mp="14v3pmpe1vuj4" />

      {/* Sales Copy */}
      <section className="bg-[#fdfbf7] border-y border-blue-100 pt-[30px] pb-0" style={{
      paddingTop: "0px"
    }} data-id="element-6" data-mp="zwsjaomys25n">
        <PainPoints data-id="element-7" data-mp="vvgp05t5asr4" />
        <DiferencialHibrido data-id="element-8" data-mp="vvyxq1wzuzfx" />
      </section>

      <WhatYouReceive data-id="element-9" data-mp="3kokl1ctix0yu" />

      {/* Testimonials */}
      <section className="py-32 relative overflow-hidden bg-gradient-to-b from-blue-50 to-sky-50 pt-[5px] pb-[5px]" data-id="element-10" data-mp="3kpdw2pzimcms">
        {/* Soft blur background elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/80 via-transparent to-transparent pointer-events-none" data-id="element-11" data-mp="1w5geqt2eu05b" />
        <div className="absolute -left-40 top-40 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-12" data-mp="254bnocwu9ocb" />
        <div className="absolute -right-40 bottom-20 w-96 h-96 bg-blue-100 rounded-full blur-3xl shadow-[0_0_100px_rgba(30,64,175,0.1)] pointer-events-none" data-id="element-13" data-mp="29x4sf0sb4woe" />

        <div className="max-container px-4 relative z-10" data-id="element-14" data-mp="2jt7zml20nli7">
          <div className="text-center mb-16" data-id="element-15" data-mp="2nvl7bf0bpthm">
            <h3 className="text-[#d97706] font-['Dancing_Script',_cursive] md:text-6xl text-center text-[32px]" data-id="element-16" data-mp="2nwntxaqm59rq">
              Comentarios destacados ⭐
            </h3>
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} className="flex items-center justify-center gap-2 mt-4 text-blue-400 font-bold uppercase tracking-widest text-[10px]" data-id="element-17" data-mp="thtrtyvbcfe9">
              <ArrowRight className="rotate-180 w-3 h-3" data-id="element-18" data-mp="1wubytqz6g69g" />
              <span data-id="element-19" data-mp="2asiju6j12brp">Deslizá para ver más</span>
              <ArrowRight className="w-3 h-3" data-id="element-20" data-mp="2aq5v9airl35o" />
            </motion.div>
          </div>

          <div className="max-w-xl mx-auto relative" data-id="element-21" data-mp="25rult0wdsunz">
            <div className="overflow-hidden" data-id="element-22" data-mp="2kpmzroma5b02">
              <motion.div key={currentTestimonial} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} onDragEnd={(_, info) => {
              if (info.offset.x > 100) {
                prevTestimonial();
              } else if (info.offset.x < -100) {
                nextTestimonial();
              }
            }} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} className="bg-white rounded-[2rem] shadow-[0_30px_60px_-12px_rgba(30,64,175,0.15)] border border-blue-100 relative backdrop-blur-sm cursor-grab active:cursor-grabbing overflow-hidden" data-id="element-23" data-mp="2kobvlnoxa18f">
                <img src={testimonials[currentTestimonial]} alt={`Testimonio ${currentTestimonial + 1}`} className="w-full h-auto object-cover" data-id="element-24" data-mp="3456es3dg9m9v" />
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-16 bg-white p-4 rounded-full shadow-xl text-blue-600 hover:bg-blue-50 transition-colors z-20 hidden md:block" data-id="element-25" data-mp="2gf2nmvhg7ezr">
              <ArrowRight className="rotate-180" size={24} data-id="element-26" data-mp="2vxlrddayv8zc" />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-16 bg-white p-4 rounded-full shadow-xl text-blue-600 hover:bg-blue-50 transition-colors z-20 hidden md:block" data-id="element-27" data-mp="2qwekrhkk8fu9">
              <ArrowRight size={24} data-id="element-28" data-mp="2d62nz5d15sws" />
            </button>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-3 mt-12" data-id="element-29" data-mp="286gd32kocor5">
              {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-blue-600' : 'w-3 bg-blue-200 hover:bg-blue-300'}`} data-id="element-30" data-mp="238538rudqe8x" data-mpi={i} />)}
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-31" data-mp="9ocvesheve6x">
            <PurchaseCTA label="Quiero mi sistema completo" data-id="element-32" data-mp="dq7vjtnlvlj4" />
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="relative overflow-hidden bg-[#fdfbf7] py-16 md:py-24" data-id="element-33" data-mp="iqm6pkki7bld">
        <GiftBackground data-id="element-34" data-mp="np765hrxlr8r" />

        <div className="max-container px-4 relative z-10" data-id="element-35" data-mp="nncm05ohi35q">
          <div className="mb-10 text-center md:mb-12" data-id="element-36" data-mp="3m6kv5sjbprfs">
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="mb-6 inline-flex items-center justify-center" data-id="element-37" data-mp="3m84ddoojick7">
              <div className="rounded-2xl bg-[#2e1065] p-4 text-white shadow-xl" data-id="element-38" data-mp="33ftes70x50rw">
                <Gift size={40} data-id="element-39" data-mp="33dpbnraug5so" />
              </div>
            </motion.div>

            <h2 className="font-['Playfair_Display',_serif] text-4xl font-bold text-[#2e1065] md:text-7xl" data-id="element-40" data-mp="38dbpa1ul3o20">
              Regalos <span className="text-[#6b4fa0]" data-id="element-41" data-mp="2f97wjbd4tien">exclusivos</span>
            </h2>
            <div className="mt-4 flex items-center justify-center gap-2" data-id="element-42" data-mp="2k4n6vtbxkw6b">
              <Sparkles className="text-[#b78b31]" size={18} data-id="element-43" data-mp="2p51eja9me43t" />
              <p className="text-sm font-black uppercase tracking-[0.2em] text-[#765e37]" data-id="element-44" data-mp="2u4e2jyc89h7z">
                100% GRATIS SOLO POR HOY
              </p>
              <Sparkles className="text-[#b78b31]" size={18} data-id="element-45" data-mp="1whykovfw2hjw" />
            </div>
          </div>

          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-3 md:gap-6" data-id="element-46" data-mp="21gi9d3lva7fy">
            <BonusCard number="01" title="Kit de encantamiento para la primera sesión" image="https://cdn.magicpatterns.com/uploads/pmRzXRZqmi8YYai2fYf7pQ/Diseno_sin_titulo_(1).webp" description="Cree una experiencia inolvidable desde el primer contacto con el cliente. Tarjetas, minicuestionario y guion de bienvenida." data-id="element-47" data-mp="26ejrnmbgy2qd" />
            <BonusCard number="02" title="Cartas terapéuticas: Ho'oponopono" image="https://cdn.magicpatterns.com/uploads/bzJHKSegp2KUJuhsotu3ia/Diseno_sin_titulo_(3).webp" description="Herramienta para sanar vínculos y liberar memorias de dolor a través del perdón y la gratitud." data-id="element-48" data-mp="3w00nlmkabm7x" />
            <BonusCard number="03" title="Biblioteca de Meditaciones Sistémicas" image="https://cdn.magicpatterns.com/uploads/viHe2PLREipfnY3drA8oSs/Diseno_sin_titulo_(4).webp" description="50 guiones listos de meditación para la integración emocional y el equilibrio del sistema familiar." data-id="element-49" data-mp="3hzhian77eu9g" />
            <BonusCard number="04" title="Diario de integración y realineación" image="https://cdn.magicpatterns.com/uploads/wi7scaPramRe8ZCZEVAC2k/Diseno_sin_titulo_(8).webp" description="Una herramienta práctica para ayudar a los clientes a mantener un movimiento terapéutico activo entre sesiones." data-id="element-50" data-mp="2za14wzaf77bm" />
            <div className="col-span-2" data-id="element-51" data-mp="2km9fosul0xqw">
              <BonusCard variant="horizontal" number="05" title="Protocolo de Protección Energética" image="https://cdn.magicpatterns.com/uploads/pVt6KcawPPmAL15TJPFkkm/Diseno_sin_titulo_(6).webp" description="Técnicas prácticas para limpiar, proteger y sostener la energía del terapeuta antes y después de las sesiones." data-id="element-52" data-mp="2knc6l0rwn06t" />
            </div>
          </div>

          <div className="mt-8 text-center md:mt-10" data-id="element-53" data-mp="2plm076xzdsqb">
            <p className="mx-auto max-w-md font-['Playfair_Display',_serif] text-lg text-[#51405f] md:text-xl" data-id="element-54" data-mp="2pl490oq6e4p9">
              5 herramientas adicionales incluidas con tu sistema, sin costo extra.
            </p>
            <div className="mt-5" data-id="element-55" data-mp="3hbii87n8usul">
              <PurchaseCTA label="Quiero mi sistema completo" data-id="element-56" data-mp="3m39xhm0y9c5f" />
            </div>
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-blue-100 relative overflow-hidden pt-[30px] pb-[0px]" data-id="element-57" data-mp="1amyiene2wtqx">
        <div className="max-w-4xl mx-auto px-4 relative z-10" data-id="element-58" data-mp="15nlx472uvuqg">
          <div className="bg-blue-950 text-white p-4 text-center font-black uppercase tracking-widest mb-8 animate-pulse" data-id="element-59" data-mp="1kdt9c529ship">
            OFERTA VÁLIDA SOLO POR HOY: {currentDate}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-60" data-mp="rqgmdts767ov">
            {['Descargue todos los archivos de FORMATO PDF, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-blue-200 shadow-sm" data-id="element-61" data-mp="2d16z9j7jwctb" data-mpi={i}>
                <CheckCircle2 className="text-blue-600 shrink-0" size={20} data-id="element-62" data-mp="hoea35k6grak" />
                <p className="text-blue-900 text-sm font-bold leading-tight" data-id="element-63" data-mp="mmxv93qy5z1v">
                  {text}
                </p>
              </div>)}
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-64" data-mp="vmcouc2eupql"></div>
        </div>
      </section>

      <UpsellPurchase data-id="element-65" data-mp="vk8b72epmcd4" />

      <section className="w-full" data-id="element-66" data-mp="10m7ekcag99a2">
        <img src="https://cdn.magicpatterns.com/uploads/ixnQeuB1GGLCojQp22tMb6/hf_20260519_161050_0e03f4a6-9ed0-4f9f-895d-8019c0d38836.webp" alt="Sofía Alondra - Creadora del Sistema Profesional de Constelación Familiar Terapéutica" className="w-full h-auto" data-id="element-67" data-mp="10l61k29vicpp" />
      </section>

      <Reviews data-id="element-68" data-mp="1ecar2bjj1omb" />

      <PortalOffer data-id="element-69" data-mp="1eewz0caby652" />

      <FAQ data-id="element-70" data-mp="1jcf7y2ockld2" />

      <footer className="py-12 border-t border-blue-100 text-center text-blue-600 text-sm" data-id="element-71" data-mp="1jcyjn1nemdin">
        <p data-id="element-72" data-mp="1oaqi6eik0xvt">
          © 2025 Sistema de Constelaciones Familiares. Todos los derechos
          reservados.
        </p>
      </footer>
    </div>;
}